import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AuthActions } from '../store/actions/authActions';
import { AuthStateData, IAuthStateData } from '../store/state/AuthStateData';
import { AuthRedirect } from './authRedirect';
import { AuthContext } from './AuthContext';
import { ApplicationState, applicationStore } from '../store/ApplicationStore';

const AuthComponent: React.FC<IAuthStateData> = (props) => {
  const { children } = props;
  const { isLoggedIn, authManager, userPermissions } = useSelector<ApplicationState, AuthStateData>((state) => state.authData);

  const history = useHistory();

  const successAuthCallback = useCallback(() => AuthActions.userSignedIn(), []);

  const onLogOut = useCallback(async () => {
    const manager = authManager || applicationStore.getState().authData.authManager;
    return await manager?.logout().then(() => {
      AuthActions.userSignedOut();
    });
  }, [authManager]);

  const isOriginPath = useCallback(() => {
    const { href, origin } = window.location;
    return (href.endsWith('/') && href.slice(0, href.length - 1) === origin) || href === origin;
  }, []);

  const runAuth = useCallback(() => {
    const auth = new AuthRedirect({
      successAuthCallback,
      errorAuthCallback: () => {},
    });
    const token = localStorage.getItem('msal.idtoken');
    if (auth.getAccount() && token) {
      AuthActions.setAuthManager(auth);
      if (!auth.accessToken) {
        auth.setAccessToken(token);
      }
      if (window.location.pathname === '/') {
        history?.push(`/home`);
      }
      successAuthCallback();
    } else {
      if (isOriginPath()) return auth.signIn();

      window.location.href = origin;
      return;
    }
  }, [props]);

  const onUnauthorizedErrorHandler = async () => onLogOut().then(() => history?.push(`/`));

  useEffect(() => {
    window.addEventListener('not_authorized', onUnauthorizedErrorHandler);
    return function () {
      window.removeEventListener('not_authorized', onUnauthorizedErrorHandler);
    };
  }, []);

  useEffect(() => runAuth(), []);

  useEffect(() => {
    AuthActions.getCurrentUserPermissions();
  }, []);

  return <AuthContext.Provider value={{ isLoggedIn, onLogOut, userPermissions }}>{isLoggedIn ? children : null}</AuthContext.Provider>;
};

export default AuthComponent;
